import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Avatar,
  useTheme,
  Paper,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Timer as TimerIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Recipe } from '../models/Recipe';
import { KitchenTimer, IngredientSubstitution } from './index';

interface RecipeSidebarProps {
  recipe: Recipe;
  currentStepIndex: number;
  currentScale: number;
  onStepClick: (stepIndex: number) => void;
  onTimerComplete: (timerId: string, timerName: string) => void;
  completedSteps?: Set<number>;
  autoStartTimer?: { duration: number; label: string } | null;
  onRecipeUpdate?: (updatedRecipe: Recipe, changes: string[]) => void;
}

const RecipeSidebar: React.FC<RecipeSidebarProps> = ({
  recipe,
  currentStepIndex,
  currentScale,
  onStepClick,
  onTimerComplete,
  completedSteps = new Set(),
  autoStartTimer,
  onRecipeUpdate,
}) => {
  const theme = useTheme();
  const [timersExpanded, setTimersExpanded] = React.useState(true); // Always expanded for visibility
  const [activeTimers, setActiveTimers] = React.useState<Array<{id: string, name: string, remaining: number, isActive: boolean, isFinished: boolean}>>([]);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Scroll active step into view only when step index changes
  React.useEffect(() => {
    if (scrollContainerRef.current && typeof currentStepIndex === 'number') {
      const container = scrollContainerRef.current;
      const stepElements = container.children;
      const activeElement = stepElements[currentStepIndex] as HTMLElement;

      if (activeElement) {
        activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentStepIndex]); // Only trigger when step index changes, not when recipe updates

  const ingredientsList = recipe.ingredients
    .split('\n')
    .map((ingredient: string) => ingredient.trim())
    .filter((ingredient: string) => ingredient);

  const totalSteps = recipe.steps?.length || 0;

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimersUpdate = (timers: Array<{id: string, name: string, remaining: number, isActive: boolean, isFinished: boolean}>) => {
    setActiveTimers(timers);
  };

  return (
    <Box
      sx={{
        p: { xs: 3, lg: 4 }, // Responsive padding to match right page
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '"Playfair Display", Georgia, serif',
        overflow: 'hidden', // Prevent overall container overflow
      }}
    >
      {/* Recipe Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <Box sx={{ mb: 3 }}>
          {recipe.image && (
            <Box
              sx={{
                width: '100%',
                height: 150,
                borderRadius: 3,
                overflow: 'hidden',
                mb: 2,
                boxShadow: theme.palette.mode === 'dark' 
                  ? '0 4px 20px rgba(0, 0, 0, 0.3)'
                  : '0 4px 20px rgba(255, 107, 53, 0.15)',
                border: 1,
                borderColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 138, 101, 0.2)'
                  : 'rgba(255, 107, 53, 0.2)',
              }}
            >
              <img
                src={recipe.image}
                alt={recipe.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          )}
          
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              fontFamily: '"Playfair Display", Georgia, serif',
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            {recipe.title}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip
              size="small"
              label={`${totalSteps} steps`}
              sx={{
                backgroundColor: 'rgba(74, 124, 89, 0.1)',
                color: 'rgb(74, 124, 89)',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
            {currentScale !== 1 && (
              <Chip
                size="small"
                label={`${currentScale}x scaled`}
                color="primary"
                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
              />
            )}
          </Box>
        </Box>
      </motion.div>

      <Divider sx={{ mb: 3, borderColor: 'rgba(255, 107, 53, 0.2)' }} />

      {/* Ingredients Section */}
      <motion.div
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 2,
            color: 'text.primary',
            fontFamily: '"Playfair Display", Georgia, serif',
            fontSize: '1.1rem',
          }}
        >
          Ingredients
          {currentScale !== 1 && (
            <Typography
              component="span"
              variant="caption"
              sx={{
                ml: 1,
                color: 'primary.main',
                fontWeight: 500,
                fontStyle: 'italic',
              }}
            >
              (scaled to {currentScale}x)
            </Typography>
          )}
        </Typography>

        <List dense sx={{ mb: 3 }}>
          {ingredientsList.map((ingredient, index) => (
            <ListItem
              key={index}
              sx={{
                px: 0,
                py: 0.5,
                borderLeft: currentScale !== 1 ? 2 : 0,
                borderColor: 'primary.light',
                pl: currentScale !== 1 ? 2 : 0,
                ml: currentScale !== 1 ? 1 : 0,
              }}
            >
              <ListItemText
                primary={ingredient}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: currentScale !== 1 ? 600 : 400,
                  color: currentScale !== 1 ? 'primary.main' : 'text.primary',
                  sx: { fontFamily: 'inherit' },
                }}
              />
            </ListItem>
          ))}
        </List>

        {/* Ingredient Substitution System */}
        <IngredientSubstitution
          ingredients={ingredientsList}
          recipeTitle={recipe.title}
          recipe={recipe}
          onRecipeUpdate={onRecipeUpdate}
          onSubstitutionSelect={(original, substitute) => {
            console.log(`Substituting "${original}" with "${substitute}"`);
          }}
        />
      </motion.div>

      <Divider sx={{ mb: 3, borderColor: 'rgba(255, 107, 53, 0.2)' }} />

      {/* Steps Navigation - Horizontal Carousel */}
      <motion.div
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        style={{ flexShrink: 0, marginBottom: 24 } as React.CSSProperties}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 2,
            color: 'text.primary',
            fontFamily: '"Playfair Display", Georgia, serif',
            fontSize: '1.1rem',
          }}
        >
          Steps
        </Typography>
        <Box
          ref={scrollContainerRef}
          sx={{
            display: 'flex',
            flexDirection: 'column', // Vertical list
            gap: 2,
            pb: 1,
            mb: 1,
            maxHeight: '400px', // Limit height but allow scrolling inside if needed
            overflowY: 'auto',
          }}
        >
          {recipe.steps?.map((step, index) => {
            const isActive = index === currentStepIndex;
            const isCompleted = completedSteps.has(index);
            const isPast = index < currentStepIndex;
            return (
              <Paper
                key={step.id}
                elevation={isActive ? 4 : 1}
                sx={{
                  width: '100%', // Full width
                  p: 2,
                  cursor: 'pointer',
                  backgroundColor: isActive
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(255, 138, 101, 0.13)'
                      : 'rgba(255, 107, 53, 0.10)'
                    : isCompleted || isPast
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(74, 124, 89, 0.10)'
                      : 'rgba(74, 124, 89, 0.07)'
                    : 'background.paper',
                  border: 2,
                  borderColor: isActive
                    ? 'primary.main'
                    : isCompleted || isPast
                    ? 'rgba(74, 124, 89, 0.3)'
                    : 'rgba(255, 107, 53, 0.1)',
                  borderRadius: 3,
                  transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                  boxShadow: isActive ? '0 4px 16px rgba(255,107,53,0.12)' : undefined,
                  '&:hover': {
                    backgroundColor: isActive
                      ? theme.palette.mode === 'dark'
                        ? 'rgba(255, 138, 101, 0.18)'
                        : 'rgba(255, 107, 53, 0.15)'
                      : theme.palette.mode === 'dark'
                      ? 'rgba(255, 138, 101, 0.08)'
                      : 'rgba(255, 107, 53, 0.08)',
                    transform: 'translateY(-2px) scale(1.03)',
                  },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  flexShrink: 0,
                }}
                onClick={() => onStepClick(index)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      backgroundColor: isActive
                        ? 'primary.main'
                        : isCompleted || isPast
                        ? 'rgb(74, 124, 89)'
                        : 'grey.300',
                      color: 'white',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      flexShrink: 0,
                      mr: 1,
                    }}
                  >
                    {isCompleted || isPast ? (
                      <CheckIcon sx={{ fontSize: 18 }} />
                    ) : (
                      index + 1
                    )}
                  </Avatar>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? 'primary.main' : 'text.primary',
                      fontFamily: '"Playfair Display", serif',
                      fontSize: '1rem',
                      mr: 1,
                    }}
                  >
                    Step {index + 1}
                  </Typography>
                </Box>
                <Typography
                  variant="body1" // Larger text
                  sx={{
                    color: isActive ? 'primary.main' : 'text.secondary',
                    fontWeight: 400,
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {step.content.slice(0, 60)}
                  {step.content.length > 60 ? '…' : ''}
                </Typography>
              </Paper>
            );
          })}
        </Box>
      </motion.div>

      {/* Kitchen Timer Section */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        style={{ flexShrink: 0 } as React.CSSProperties}
      >
        <Divider sx={{ my: 2, borderColor: 'rgba(255, 107, 53, 0.2)' }} />
        
        <Paper
          sx={{
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 138, 101, 0.05)'
              : 'rgba(255, 107, 53, 0.03)',
            border: 1,
            borderColor: 'rgba(255, 107, 53, 0.2)',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              cursor: 'pointer',
            }}
            onClick={() => setTimersExpanded(!timersExpanded)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
              <TimerIcon color="primary" sx={{ fontSize: 20 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontFamily: '"Playfair Display", Georgia, serif',
                }}
              >
                Kitchen Timers
              </Typography>
              
              {/* Active Timer Indicators when collapsed */}
              {!timersExpanded && activeTimers.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {activeTimers.slice(0, 2).map(timer => (
                    <Chip
                      key={timer.id}
                      size="small"
                      label={timer.isFinished ? 'Done!' : formatTime(timer.remaining)}
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        backgroundColor: timer.isFinished 
                          ? 'success.main'
                          : timer.isActive 
                            ? 'primary.main'
                            : 'grey.300',
                        color: 'white',
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: 1 }
                      }}
                    />
                  ))}
                  {activeTimers.length > 2 && (
                    <Chip
                      size="small"
                      label={`+${activeTimers.length - 2}`}
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        backgroundColor: 'grey.400',
                        color: 'white',
                        '& .MuiChip-label': { px: 1 }
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
            <IconButton size="small" sx={{ color: 'primary.main' }}>
              {timersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={timersExpanded}>
            <Box sx={{ px: 2, pb: 2 }}>
              <KitchenTimer
                stepName={`Step ${currentStepIndex + 1}`}
                onTimerComplete={onTimerComplete}
                autoStartTimer={autoStartTimer}
                onTimersUpdate={handleTimersUpdate}
              />
            </Box>
          </Collapse>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default React.memo(RecipeSidebar, (prevProps, nextProps) => {
  return (
    prevProps.recipe?.id === nextProps.recipe?.id &&
    prevProps.currentStepIndex === nextProps.currentStepIndex &&
    prevProps.currentScale === nextProps.currentScale &&
    JSON.stringify(prevProps.recipe?.steps) === JSON.stringify(nextProps.recipe?.steps)
  );
});
